import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import {
  Auth,
  getRedirectResult,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  UserCredential
} from '@angular/fire/auth';
import { filter } from 'rxjs';

import { AnalyticsService } from './analytics.service';
import { authErrorMessage } from '../util/auth-error-message';
import {
  googleSsoAllowedDomains,
  googleSsoUsesRedirect,
  isGoogleSsoEmail
} from '../util/google-sso';

const PENDING_SIGN_IN_KEY = 'tracit.google-sign-in-redirect';

interface PendingGoogleSignIn {
  email: string;
}

export interface GoogleSignInAttempt {
  /** Set when the popup flow signed a user in and the SSO rules passed. */
  userCredential: UserCredential | null;
  errorMessage: string | null;
}

export interface GoogleRedirectSignInResult {
  email: string | null;
  errorMessage: string | null;
  signedIn: boolean;
  /**
   * Set when the redirect completed with a second-factor challenge instead of a
   * session. The caller resolves it rather than showing an error: the Google
   * credentials were accepted, they just need a TOTP code to finish.
   */
  mfaError: unknown | null;
}

/**
 * Runs Google sign in with whichever flow the current host supports, see googleSsoUsesRedirect.
 * The deployed sites redirect; anything served from somewhere other than authDomain, localhost
 * included whichever project it is pointed at, uses a popup.
 *
 * Under the redirect flow the browser leaves the app and comes back to the same URL with a session
 * already restored. The SSO rules have to be applied before the router runs its guards, otherwise
 * the sign in route's guard sends that restored session straight to the dashboard without the
 * email domain and admin claim ever being checked. complete() is therefore run by an app
 * initializer, and SignInComponent just displays whatever it left behind. Under the popup flow the
 * credential comes straight back to SignInComponent and none of that applies.
 */
@Injectable({
  providedIn: 'root'
})
export class GoogleSignInService {
  private readonly auth = inject(Auth);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly router = inject(Router);
  private readonly analyticsService = inject(AnalyticsService);

  private email: string | null = null;
  private errorMessage: string | null = null;
  private signedIn = false;
  private mfaError: unknown | null = null;
  private keepBootIndicatorUntilNavigation = false;

  constructor() {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(event => {
        if (!this.keepBootIndicatorUntilNavigation) {
          return;
        }

        if (!this.isSignInUrl(event.urlAfterRedirects)) {
          this.keepBootIndicatorUntilNavigation = false;
          this.hideBootIndicator();
        }
      });
  }

  /** Decided from the host the app is being served on, not from the environment it was built for. */
  private get usesRedirect(): boolean {
    return isPlatformBrowser(this.platformId)
      && googleSsoUsesRedirect(window.location.hostname);
  }

  /**
   * Starts a sign in. Under the popup flow the returned attempt says how it went. Under the
   * redirect flow the browser leaves the page instead, so this only settles when the redirect
   * could not be started, by rejecting.
   */
  async start(email: string): Promise<GoogleSignInAttempt> {
    const provider = new GoogleAuthProvider();
    const customParameters: Record<string, string> = {
      prompt: 'select_account',
    };

    if (email) {
      customParameters['login_hint'] = email;
    }

    if (googleSsoAllowedDomains.length === 1) {
      customParameters['hd'] = googleSsoAllowedDomains[0];
    }

    provider.setCustomParameters(customParameters);

    if (!this.usesRedirect) {
      const userCredential = await signInWithPopup(this.auth, provider);
      const errorMessage = await this.applySsoRules(userCredential);

      return {
        userCredential: errorMessage ? null : userCredential,
        errorMessage,
      };
    }

    this.writePending({ email });

    try {
      await signInWithRedirect(this.auth, provider);
    } catch (error) {
      this.clearPending();
      throw error;
    }

    return { userCredential: null, errorMessage: null };
  }

  /** Resolves a sign in the browser has been redirected back from, before the router starts. */
  async complete(): Promise<void> {
    const pending = this.takePending();

    this.email = pending?.email ?? null;
    this.signedIn = false;

    try {
      const userCredential = await getRedirectResult(this.auth);

      if (!pending && !userCredential) {
        return;
      }

      if (!userCredential) {
        // Back on the sign in page with nothing to resolve: the user backed out at Google, or the
        // browser blocked the storage the redirect flow uses to hand the result back to the app.
        this.errorMessage = 'Google sign-in was not completed. Please try again.';
        return;
      }

      this.email = pending?.email ?? userCredential.user.email;
      this.errorMessage = await this.applySsoRules(userCredential);
      this.signedIn = this.errorMessage === null;

      if (this.signedIn) {
        // The redirect flow finishes the sign in here rather than in SignInComponent,
        // so the login event has to be logged here too or redirect sign ins go unrecorded.
        this.analyticsService.logLogin('google');
      }
    } catch (error: any) {
      if (error?.code === 'auth/multi-factor-auth-required') {
        // Not a failure: the account has 2FA enrolled, so the sign-in is finished
        // by resolving the challenge with a TOTP code. Hand the error to the
        // caller intact rather than flattening it into a message. Checked before
        // the pending record, which private browsing can lose: the caller still
        // needs the challenge to prompt for a code.
        this.mfaError = error;
        return;
      }

      if (!pending) {
        this.hideBootIndicator();
        return;
      }

      console.error('Google redirect sign-in failed', {
        code: error?.code,
        message: error?.message ?? '',
        customData: error?.customData,
      });

      this.errorMessage = authErrorMessage(error);
    } finally {
      if (this.signedIn) {
        this.keepBootIndicatorUntilNavigation = true;
        return;
      }

      this.hideBootIndicator();
    }
  }

  /** Reads and clears the outcome of the last redirect attempt. */
  consumeResult(): GoogleRedirectSignInResult {
    const result: GoogleRedirectSignInResult = {
      email: this.email,
      errorMessage: this.errorMessage,
      signedIn: this.signedIn,
      mfaError: this.mfaError,
    };

    this.email = null;
    this.errorMessage = null;
    this.signedIn = false;
    this.mfaError = null;

    return result;
  }

  /** Signs a user back out if they are not allowed in, and says why. */
  private async applySsoRules(userCredential: UserCredential): Promise<string | null> {
    if (!this.isGoogleCredential(userCredential)) {
      await signOut(this.auth);
      return 'Use the Google sign in button to continue with this email.';
    }

    if (!isGoogleSsoEmail(userCredential.user.email)) {
      await signOut(this.auth);
      return 'Use your TRACit email address to sign in with Google.';
    }

    // Every staff account signs in with Google; being staff does not make a
    // user a system admin. Admin access is decided per request by the isAdmin
    // claim paired with a staff email, not gated at the sign-in door.
    return null;
  }

  private isGoogleCredential(userCredential: UserCredential): boolean {
    return userCredential.providerId === GoogleAuthProvider.PROVIDER_ID
      || userCredential.user.providerData.some(provider => provider.providerId === GoogleAuthProvider.PROVIDER_ID);
  }

  private writePending(pending: PendingGoogleSignIn) {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    try {
      sessionStorage.setItem(PENDING_SIGN_IN_KEY, JSON.stringify(pending));
      document.documentElement.classList.add('tracit-google-sign-in-pending');
    } catch {
      // Storage can be unavailable (private browsing); the redirect result is
      // still resolved on return, but we lose the email we started with.
    }
  }

  private takePending(): PendingGoogleSignIn | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }

    try {
      const stored = sessionStorage.getItem(PENDING_SIGN_IN_KEY);
      sessionStorage.removeItem(PENDING_SIGN_IN_KEY);

      return stored ? JSON.parse(stored) as PendingGoogleSignIn : null;
    } catch {
      return null;
    }
  }

  private clearPending() {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    try {
      sessionStorage.removeItem(PENDING_SIGN_IN_KEY);
      this.keepBootIndicatorUntilNavigation = false;
      this.hideBootIndicator();
    } catch {
      // Nothing to clean up if storage is unavailable.
    }
  }

  private hideBootIndicator() {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    document.documentElement.classList.remove('tracit-google-sign-in-pending');
  }

  private isSignInUrl(url: string): boolean {
    return url === '/signin' || url.startsWith('/signin;') || url.startsWith('/signin?');
  }
}
